import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Copy, Download, LoaderCircle, Printer, QrCode } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Room } from "@/types/room";
import {
  buildRoomQrUrl,
  downloadRoomQrPdf,
  getRoomDisplayName,
  getRoomQrFileName,
  getTenantIdFromStorage,
  printRoomQrSticker,
} from "@/utils/roomQr";
import { copyToClipboard } from "@/utils/clipboard";

interface RoomQrDialogProps {
  room: Pick<Room, "id" | "number">;
  hotelId: string;
  trigger?: React.ReactNode;
}

export const RoomQrDialog: React.FC<RoomQrDialogProps> = ({
  room,
  hotelId,
  trigger,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const title = useMemo(() => getRoomDisplayName(room), [room]);
  const fileName = useMemo(() => getRoomQrFileName(room), [room]);
  const tenantId = getTenantIdFromStorage();

  const qrUrl = useMemo(() => {
    if (!tenantId) {
      return "";
    }

    try {
      return buildRoomQrUrl({ hotelId, tenantId, roomId: room.id });
    } catch {
      return "";
    }
  }, [hotelId, room.id, tenantId]);

  useEffect(() => {
    if (!open || !qrUrl) {
      return;
    }

    let isMounted = true;

    const generateQr = async () => {
      try {
        setIsGenerating(true);
        const dataUrl = await QRCode.toDataURL(qrUrl, {
          width: 320,
          margin: 1,
        });

        if (isMounted) {
          setQrDataUrl(dataUrl);
        }
      } catch (error) {
        console.error("Failed to generate QR code", error);
        toast.error(
          t("reservation.rooms.qr.generateError", {
            defaultValue: "Failed to generate QR code",
          }),
        );
      } finally {
        if (isMounted) {
          setIsGenerating(false);
        }
      }
    };

    void generateQr();

    return () => {
      isMounted = false;
    };
  }, [open, qrUrl, t]);

  const showConfigurationError = () => {
    if (!tenantId) {
      toast.error(
        t("reservation.rooms.qr.missingTenantId", {
          defaultValue: "Tenant ID is missing",
        }),
      );
      return true;
    }

    if (!qrUrl) {
      toast.error(
        t("reservation.rooms.qr.missingMobileAppUrl", {
          defaultValue: "MOBILE_APP_URL is not configured",
        }),
      );
      return true;
    }

    return false;
  };

  const handlePrint = () => {
    if (showConfigurationError() || !qrDataUrl) {
      return;
    }

    try {
      printRoomQrSticker({ title, qrDataUrl });
    } catch (error) {
      console.error("Failed to print QR sticker", error);
      toast.error(
        t("reservation.rooms.qr.printError", {
          defaultValue: "Failed to print QR sticker",
        }),
      );
    }
  };

  const handleDownloadPdf = async () => {
    if (showConfigurationError() || !qrDataUrl) {
      return;
    }

    try {
      await downloadRoomQrPdf({ title, qrDataUrl, fileName });
    } catch (error) {
      console.error("Failed to download QR PDF", error);
      toast.error(
        t("reservation.rooms.qr.downloadError", {
          defaultValue: "Failed to download QR PDF",
        }),
      );
    }
  };

  const handleCopyLink = async () => {
    if (showConfigurationError() || !qrUrl) {
      return;
    }

    const copied = await copyToClipboard(qrUrl);

    if (copied) {
      toast.success(
        t("reservation.rooms.qr.copySuccess", {
          defaultValue: "Room link copied",
        }),
      );
      return;
    }

    toast.error(
      t("reservation.rooms.qr.copyError", {
        defaultValue: "Failed to copy room link",
      }),
    );
  };

  const configurationErrorMessage = tenantId
    ? t("reservation.rooms.qr.configurationError", {
        defaultValue: "Unable to prepare QR sticker. Check MOBILE_APP_URL.",
      })
    : t("reservation.rooms.qr.tenantError", {
        defaultValue: "Unable to prepare QR sticker. Tenant ID is missing.",
      });

  let qrContent: React.ReactNode;

  if (isGenerating) {
    qrContent = <LoaderCircle className="h-8 w-8 animate-spin text-gray-500" />;
  } else if (qrDataUrl) {
    qrContent = (
      <img
        src={qrDataUrl}
        alt={`${title} QR code`}
        className="h-72 w-72 object-contain"
      />
    );
  } else {
    qrContent = (
      <div className="text-sm text-red-600">{configurationErrorMessage}</div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button type="button" variant="outline" size="sm">
            <QrCode className="h-4 w-4" />
            {t("reservation.rooms.qr.trigger", { defaultValue: "QR" })}
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-xl">
        <DialogHeader className="items-center text-center">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t("reservation.rooms.qr.description", {
              defaultValue: "Scan the QR code to open the guest app screen.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-6 py-2">
          <div className="flex min-h-80 w-full items-center justify-center rounded-xl border bg-white p-6">
            {qrContent}
          </div>

          <div className="flex w-full justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleCopyLink()}
              disabled={!qrUrl || isGenerating}
            >
              <Copy className="h-4 w-4" />
              {t("reservation.rooms.qr.copy", { defaultValue: "Copy link" })}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handlePrint}
              disabled={!qrDataUrl || isGenerating}
            >
              <Printer className="h-4 w-4" />
              {t("reservation.rooms.qr.print", { defaultValue: "Print" })}
            </Button>
            <Button
              type="button"
              onClick={() => void handleDownloadPdf()}
              disabled={!qrDataUrl || isGenerating}
            >
              <Download className="h-4 w-4" />
              {t("reservation.rooms.qr.download", {
                defaultValue: "Download PDF",
              })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
